import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Share2, MessageCircle, Leaf, Trees, Wind, Award } from "lucide-react";
import { api, API } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageTitle, Card } from "@/components/Layout";
import { SCRAP_ICONS } from "@/components/ScrapPicker";
import { drawImpact, shareText, shareLink } from "@/lib/shareImpact";

export default function Impact() {
  const { t, user } = useApp();
  const [d, setD] = useState(null);
  const [code, setCode] = useState("");
  useEffect(() => {
    api.get("/impact").then((r) => setD(r.data)).catch(() => {});
    api.get("/referrals").then((r) => setCode(r.data.code)).catch(() => {});
  }, []);
  if (!d) return <p className="text-[#7B968B]">{t("Loading…")}</p>;
  const max = Math.max(1, ...Object.values(d.by_type));
  const first = (user?.full_name || user?.business_name || "").split(" ")[0];

  const shareImage = async () => {
    const blob = await drawImpact(d, code);
    const file = new File([blob], `kindsaver-impact-${d.certificate?.no || code}.png`, { type: "image/png" });
    if (navigator.canShare?.({ files: [file] })) {
      try { await navigator.share({ files: [file], text: shareText(d, code, first) }); } catch {}
    } else {
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = file.name; a.click();
      toast.success(t("Image downloaded. You can share it from your gallery."));
    }
  };
  const shareMsg = () => window.open(`https://wa.me/?text=${encodeURIComponent(shareText(d, code, first))}`, "_blank");
  const copy = () => navigator.clipboard?.writeText(shareLink(code)).then(() => toast.success(t("Invite link copied")));

  return (
    <div className="space-y-5">
      <PageTitle sub={t("Based on the actual weight our team recorded at each pickup.")}>{t("My impact")}</PageTitle>
      <div className="ks-grain rounded-3xl bg-[#0D3B2B] p-6 text-white">
        <Leaf className="mb-2 h-7 w-7 text-[#2E9E6B]" />
        <p className="font-display text-5xl font-extrabold" data-testid="impact-total-kg">{d.total_kg} kg</p>
        <p className="text-white/70">{t("recycled in")} <span data-testid="impact-completed">{d.completed}</span> {t("completed pickups")}</p>
      </div>
      <Card className="space-y-3" data-testid="impact-by-type">
        <h2 className="font-display text-lg font-bold text-[#0D3B2B]">{t("By scrap type")}</h2>
        {!Object.keys(d.by_type).length && <p className="text-sm text-[#4A6358]">{t("Your numbers will appear here after your first completed pickup.")}</p>}
        {Object.entries(d.by_type).sort((a, b) => b[1] - a[1]).map(([k, v]) => {
          const Icon = SCRAP_ICONS[k] || Leaf;
          return (
            <div key={k} className="flex items-center gap-3">
              <Icon className="h-5 w-5 text-[#2E9E6B]" />
              <div className="flex-1">
                <div className="flex justify-between text-sm"><span className="font-semibold">{t(k)}</span><span>{v} kg</span></div>
                <div className="mt-1 h-2 rounded-full bg-[#EAEFEA]"><div className="h-2 rounded-full bg-[#2E9E6B]" style={{ width: `${(v / max) * 100}%` }} /></div>
              </div>
            </div>
          );
        })}
      </Card>
      <div className="grid grid-cols-2 gap-3">
        <Card className="p-4"><Wind className="mb-1 h-5 w-5 text-[#E05C3A]" /><p className="font-display text-2xl font-extrabold" data-testid="impact-co2">{d.co2_kg} kg</p><p className="text-xs text-[#4A6358]">{t("CO2 avoided")}</p></Card>
        <Card className="p-4"><Trees className="mb-1 h-5 w-5 text-[#2E9E6B]" /><p className="font-display text-2xl font-extrabold" data-testid="impact-trees">{d.trees}</p><p className="text-xs text-[#4A6358]">{t("trees saved")}</p></Card>
      </div>
      <p className="text-xs text-[#7B968B]">{t("CO2 and trees are estimates using conversion factors set by the Kindsaver team.")}</p>
      <div className="relative overflow-hidden rounded-3xl bg-[#E05C3A] p-5 text-white" data-testid="impact-share-card">
        <div className="absolute -right-10 -top-10 h-36 w-36 rounded-full bg-white/10" />
        <p className="text-xs font-bold uppercase tracking-widest text-white/85">{t("Inspire your friends")}</p>
        <p className="mt-1 font-display text-xl font-extrabold">{t("Share your impact and invite friends")}</p>
        <p className="mt-1 text-sm text-white/90">{t("Your share includes your referral code. Earn a reward when friends complete their first pickup.")}</p>
        {code && <button onClick={copy} className="mt-3 rounded-2xl border-2 border-dashed border-white/80 px-4 py-2 font-display text-2xl font-extrabold tracking-widest" data-testid="impact-ref-code">{code}</button>}
        <div className="mt-4 grid grid-cols-2 gap-3">
          <button onClick={shareImage} disabled={!code} data-testid="impact-share-image-btn" className="flex h-14 items-center justify-center gap-2 rounded-2xl bg-white font-bold text-[#0D3B2B] active:scale-95 transition-transform disabled:opacity-60"><Share2 className="h-5 w-5" />{t("Share image")}</button>
          <button onClick={shareMsg} disabled={!code} data-testid="impact-share-msg-btn" className="flex h-14 items-center justify-center gap-2 rounded-2xl bg-[#0D3B2B] font-bold text-white active:scale-95 transition-transform disabled:opacity-60"><MessageCircle className="h-5 w-5" />{t("WhatsApp")}</button>
        </div>
      </div>
      <Card className="space-y-3" data-testid="impact-certificate-card">
        <h2 className="flex items-center gap-2 font-display text-lg font-bold text-[#0D3B2B]"><Award className="h-5 w-5 text-[#E05C3A]" />{t("Impact certificate")}</h2>
        <p className="text-sm text-[#4A6358]">{t("A PDF certificate of the waste you recycled with us. Useful for ESG / CSR reports, audits and your notice board.")}</p>
        <div className="flex gap-2">
          {[0, 1].map((k) => { const y = new Date().getFullYear() - k; return (
            <a key={y} href={`${API}/certificate?year=${y}`} data-testid={`impact-certificate-${y}`}
              className="flex h-12 flex-1 items-center justify-center rounded-2xl border-2 border-[#0D3B2B] font-semibold text-[#0D3B2B]">{t("Download")} {y}</a>
          ); })}
        </div>
      </Card>
    </div>
  );
}
